import { FormControl, FormGroup, Validators } from '@angular/forms';

import { SETTINGS_FORM_GROUP_NAME } from '../../constants';
import { UpsertUserData } from '../upsert-user';
import { UpsertMerchantUserSettings } from '../upsert-user-merchant';
import { UserRole } from '../user-role';

import { UserBaseForm, UserBaseFormControls } from './user-base.form';

export enum UserMerchantSettingsFormControls {
    TeamLeadId = 'teamLeadId',
    HoldWithdrawal = 'holdWithdrawal',
    StaticCommissionRateSettings = 'staticCommissionRateSettings',
    LadderCommissionRateSettings = 'ladderCommissionRateSettings'
}

export enum UserMerchantStaticCommissionRateFormControls {
    Active = 'active',
    StaticMerchantPayinCommissionRate = 'staticMerchantPayinCommissionRate',
    StaticMerchantPayoutCommissionRate = 'staticMerchantPayoutCommissionRate'
}

export enum UserMerchantLadderCommissionRateFormControls {
    Active = 'active',
    FirstLevelMerchantPayinCommissionRate = 'firstLevelMerchantPayinCommissionRate',
    FirstLevelMerchantPayoutCommissionRate = 'firstLevelMerchantPayoutCommissionRate',
    FirstLevelMonthlyVolume = 'firstLevelMonthlyVolume',
    SecondLevelMerchantPayinCommissionRate = 'secondLevelMerchantPayinCommissionRate',
    SecondLevelMerchantPayoutCommissionRate = 'secondLevelMerchantPayoutCommissionRate',
    SecondLevelMonthlyVolume = 'secondLevelMonthlyVolume',
    ThirdLevelMerchantPayinCommissionRate = 'thirdLevelMerchantPayinCommissionRate',
    ThirdLevelMerchantPayoutCommissionRate = 'thirdLevelMerchantPayoutCommissionRate',
    ThirdLevelMonthlyVolume = 'thirdLevelMonthlyVolume'
}

export class UserMerchantForm extends UserBaseForm {
    constructor() {
        super();
    }

    initForm(): void {
        this.form.get(UserBaseFormControls.Role)?.setValue(UserRole.Merchant, { emitEvent: false });
        this.form.addControl(UserMerchantSettingsFormControls.TeamLeadId, new FormControl(null), { emitEvent: false });
        this.form.addControl(
            SETTINGS_FORM_GROUP_NAME,
            new FormGroup({
                [UserMerchantSettingsFormControls.HoldWithdrawal]: new FormControl('1', [Validators.required]),
                [UserMerchantSettingsFormControls.StaticCommissionRateSettings]: new FormGroup({
                    [UserMerchantStaticCommissionRateFormControls.Active]: new FormControl(true),
                    [UserMerchantStaticCommissionRateFormControls.StaticMerchantPayinCommissionRate]: new FormControl(
                        '0',
                        [Validators.required]
                    ),
                    [UserMerchantStaticCommissionRateFormControls.StaticMerchantPayoutCommissionRate]: new FormControl(
                        '0',
                        [Validators.required]
                    )
                }),
                [UserMerchantSettingsFormControls.LadderCommissionRateSettings]: new FormGroup({
                    [UserMerchantLadderCommissionRateFormControls.Active]: new FormControl(false),
                    [UserMerchantLadderCommissionRateFormControls.FirstLevelMerchantPayinCommissionRate]:
                        new FormControl('0', [Validators.required]),
                    [UserMerchantLadderCommissionRateFormControls.FirstLevelMerchantPayoutCommissionRate]:
                        new FormControl('0', [Validators.required]),
                    [UserMerchantLadderCommissionRateFormControls.FirstLevelMonthlyVolume]: new FormControl('0', [
                        Validators.required
                    ]),
                    [UserMerchantLadderCommissionRateFormControls.SecondLevelMerchantPayinCommissionRate]:
                        new FormControl('0', [Validators.required]),
                    [UserMerchantLadderCommissionRateFormControls.SecondLevelMerchantPayoutCommissionRate]:
                        new FormControl('0', [Validators.required]),
                    [UserMerchantLadderCommissionRateFormControls.SecondLevelMonthlyVolume]: new FormControl('0', [
                        Validators.required
                    ]),
                    [UserMerchantLadderCommissionRateFormControls.ThirdLevelMerchantPayinCommissionRate]:
                        new FormControl('0', [Validators.required]),
                    [UserMerchantLadderCommissionRateFormControls.ThirdLevelMerchantPayoutCommissionRate]:
                        new FormControl('0', [Validators.required]),
                    [UserMerchantLadderCommissionRateFormControls.ThirdLevelMonthlyVolume]: new FormControl('0', [
                        Validators.required
                    ])
                })
            })
        );
    }

    resetSettingsByType(
        settingsType:
            | UserMerchantSettingsFormControls.StaticCommissionRateSettings
            | UserMerchantSettingsFormControls.LadderCommissionRateSettings
    ): void {
        const settingsControl = this.form.get(SETTINGS_FORM_GROUP_NAME);
        const staticCommissionControl = settingsControl?.get(
            UserMerchantSettingsFormControls.StaticCommissionRateSettings
        );

        if (settingsType === UserMerchantSettingsFormControls.StaticCommissionRateSettings) {
            staticCommissionControl
                ?.get(UserMerchantStaticCommissionRateFormControls.StaticMerchantPayinCommissionRate)
                ?.setValue('0', { emitEvent: false });
            staticCommissionControl
                ?.get(UserMerchantStaticCommissionRateFormControls.StaticMerchantPayoutCommissionRate)
                ?.setValue('0', { emitEvent: false });

            return;
        }

        const ladderCommissionControl = settingsControl?.get(
            UserMerchantSettingsFormControls.LadderCommissionRateSettings
        );

        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.FirstLevelMerchantPayinCommissionRate)
            ?.setValue('0', {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.FirstLevelMerchantPayoutCommissionRate)
            ?.setValue('0', {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.FirstLevelMonthlyVolume)
            ?.setValue('0', { emitEvent: false });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.SecondLevelMerchantPayinCommissionRate)
            ?.setValue('0', {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.SecondLevelMerchantPayoutCommissionRate)
            ?.setValue('0', {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.SecondLevelMonthlyVolume)
            ?.setValue('0', { emitEvent: false });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.ThirdLevelMerchantPayinCommissionRate)
            ?.setValue('0', {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.ThirdLevelMerchantPayoutCommissionRate)
            ?.setValue('0', {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.ThirdLevelMonthlyVolume)
            ?.setValue('0', { emitEvent: false });
    }

    override applyUserDetails(userDetails?: UpsertUserData) {
        const settings = userDetails?.settings as UpsertMerchantUserSettings;
        const settingsControl = this.form.get(SETTINGS_FORM_GROUP_NAME);
        const staticCommissionControl = settingsControl?.get(
            UserMerchantSettingsFormControls.StaticCommissionRateSettings
        );
        const ladderCommissionControl = settingsControl?.get(
            UserMerchantSettingsFormControls.LadderCommissionRateSettings
        );

        settingsControl
            ?.get(UserMerchantSettingsFormControls.HoldWithdrawal)
            ?.setValue(settings.holdWithdrawal, { emitEvent: false });
        staticCommissionControl
            ?.get(UserMerchantStaticCommissionRateFormControls.Active)
            ?.setValue(settings.staticCommissionRateSettings.active, { emitEvent: false });
        staticCommissionControl
            ?.get(UserMerchantStaticCommissionRateFormControls.StaticMerchantPayinCommissionRate)
            ?.setValue(settings.staticCommissionRateSettings.staticMerchantPayinCommissionRate, { emitEvent: false });
        staticCommissionControl
            ?.get(UserMerchantStaticCommissionRateFormControls.StaticMerchantPayoutCommissionRate)
            ?.setValue(settings.staticCommissionRateSettings.staticMerchantPayoutCommissionRate, { emitEvent: false });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.Active)
            ?.setValue(settings.ladderCommissionRateSettings.active, { emitEvent: false });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.FirstLevelMerchantPayinCommissionRate)
            ?.setValue(settings.ladderCommissionRateSettings.firstLevelMerchantPayinCommissionRate, {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.FirstLevelMerchantPayoutCommissionRate)
            ?.setValue(settings.ladderCommissionRateSettings.firstLevelMerchantPayoutCommissionRate, {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.FirstLevelMonthlyVolume)
            ?.setValue(settings.ladderCommissionRateSettings.firstLevelMonthlyVolume, { emitEvent: false });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.SecondLevelMerchantPayinCommissionRate)
            ?.setValue(settings.ladderCommissionRateSettings.secondLevelMerchantPayinCommissionRate, {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.SecondLevelMerchantPayoutCommissionRate)
            ?.setValue(settings.ladderCommissionRateSettings.secondLevelMerchantPayoutCommissionRate, {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.SecondLevelMonthlyVolume)
            ?.setValue(settings.ladderCommissionRateSettings.secondLevelMonthlyVolume, { emitEvent: false });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.ThirdLevelMerchantPayinCommissionRate)
            ?.setValue(settings.ladderCommissionRateSettings.thirdLevelMerchantPayinCommissionRate, {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.ThirdLevelMerchantPayoutCommissionRate)
            ?.setValue(settings.ladderCommissionRateSettings.thirdLevelMerchantPayoutCommissionRate, {
                emitEvent: false
            });
        ladderCommissionControl
            ?.get(UserMerchantLadderCommissionRateFormControls.ThirdLevelMonthlyVolume)
            ?.setValue(settings.ladderCommissionRateSettings.thirdLevelMonthlyVolume, { emitEvent: false });

        super.applyUserDetails(userDetails);
    }
}
